import { SettingOutlined } from "@ant-design/icons";
import { Alert, Button, Form, InputNumber, Modal, Tooltip, message } from "antd";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useQuery, useQueryClient } from "react-query";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { serverUrl } from "../../../../../nestserver";
import { selectSchoolId } from "../../../../../redux/slices/auth.slice";
import AdmissionApplicationPage from "./AdmissionApplicationPage";
import DownloadApplication from "./DownloadApplication";
import AdmissionInquiry, {
  INQUIRY_QUERY_KEY,
} from "../../components/admission/AdmissionInquiry";
import { default as AdmissionHomePage } from "./AdmissionHomePage";

const SwitchAdmission = () => {
  // The active tab lives in the URL (?tab=) so it survives navigating into a
  // detail screen and back.
  const [searchParams, setSearchParams] = useSearchParams();
  const step = Number(searchParams.get("tab")) || 1;
  const setStep = (n) =>
    setSearchParams(n === 1 ? {} : { tab: String(n) }, { replace: true });
  const [feeSettingsOpen, setFeeSettingsOpen] = useState(false);
  const [feeForm] = Form.useForm();
  const schoolId = useSelector(selectSchoolId);
  const queryClient = useQueryClient();

  const schoolQuery = useQuery(
    ["school", schoolId],
    () =>
      axios
        .get(`${serverUrl}/school/${schoolId}`, {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        })
        .then((res) => res.data),
    { enabled: !!schoolId }
  );
  const school = schoolQuery.data?.result?.data;

  useEffect(() => {
    if (school) {
      feeForm.setFieldsValue({ application_fee: school.application_fee || 0 });
    }
  }, [school, feeForm]);

  const saveFee = async (values) => {
    // UpdateSchoolsDto requires the whole school profile, not just the fee
    // — reuse the already-fetched record and only actually change the fee.
    const payload = {
      name: school?.name,
      address: school?.address,
      district: school?.district,
      email: school?.email,
      mandal: school?.mandal,
      phone: school?.phone,
      city: school?.city,
      pincode: school?.pincode,
      refId: school?.refId,
      state: school?.state,
      website: school?.website,
      owner: school?.owner,
      head_master: school?.head_master,
      logo: school?.logo,
      is_transportation_enabled: school?.is_transportation_enabled,
      is_live_tracking_enabled: school?.is_live_tracking_enabled,
      is_montessori_enabled: school?.is_montessori_enabled,
      application_fee: values.application_fee,
    };
    try {
      await axios.put(`${serverUrl}/school/${schoolId}`, payload, {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      });
      message.success("Application fee updated");
      queryClient.invalidateQueries(["school", schoolId]);
      setFeeSettingsOpen(false);
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Could not update the application fee"
      );
    }
  };

  // A fee that's already set (non-zero) may already be in effect on
  // applications that were charged under it — changing it silently could
  // look like a bug to whoever set it originally, so the change is
  // confirmed explicitly instead of saving straight away.
  const handleSaveFee = (values) => {
    const previousFee = school?.application_fee || 0;
    if (previousFee > 0 && values.application_fee !== previousFee) {
      Modal.confirm({
        title: "Change application fee?",
        content: `An application fee of ₹${previousFee} is already set and may already apply to existing applications. Changing it to ₹${values.application_fee} will only affect new applications submitted from now on.`,
        okText: "Yes, change it",
        cancelText: "Cancel",
        onOk: () => saveFee(values),
      });
    } else {
      saveFee(values);
    }
  };

  // Shared with AdmissionHomePage's own fetch via the same react-query key,
  // so this doesn't cost an extra network round trip — it's just used here
  // to show live counts on the Pending/History tabs.
  async function fetchApplications() {
    const res = await axios({
      method: "get",
      url: serverUrl + "/admin/application/list/",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data;
  }
  const applications = useQuery("applications", fetchApplications);
  const allApplications = applications.data?.data || [];
  const pendingCount = allApplications.filter(
    (app) => app.status === "pending"
  ).length;
  const historyCount = allApplications.length - pendingCount;

  // Open (not yet converted or closed) inquiries for the tab badge.
  const inquiryCounts = useQuery([INQUIRY_QUERY_KEY, "counts"], () =>
    axios
      .get(`${serverUrl}/admission-inquiry`, {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      })
      .then((res) => res.data?.counts || {})
  );
  const openInquiryCount =
    (inquiryCounts.data?.new || 0) +
    (inquiryCounts.data?.contacted || 0) +
    (inquiryCounts.data?.visited || 0);

  // Solid, semantic-colored pill instead of a translucent-white one — the
  // old badge (rgba(255,255,255,0.25)) only read well on the active tab's
  // dark background; on an inactive white tab it nearly disappeared.
  const TabCount = ({ count, color }) => (
    <span
      style={{
        marginLeft: "0.5rem",
        minWidth: "1.4rem",
        height: "1.4rem",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "0 0.4rem",
        fontSize: "0.72rem",
        fontWeight: 700,
        lineHeight: 1,
        color: "#fff",
        background: color,
        borderRadius: "999px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
      }}
    >
      {count}
    </span>
  );

  function switchTab() {
    switch (step) {
      case 1:
        return (
          <div>
            <AdmissionApplicationPage />
          </div>
        );

      case 2:
        return (
          <div>
            <AdmissionHomePage key="pending" statusScope="pending" />
          </div>
        );

      case 3:
        return (
          <div>
            <AdmissionHomePage key="history" statusScope="history" />
          </div>
        );

      case 4:
        return (
          <div>
            <DownloadApplication />
          </div>
        );

      case 5:
        return (
          <div>
            <AdmissionInquiry />
          </div>
        );

      default:
        return (
          <div>
            <AdmissionHomePage statusScope="pending" />
          </div>
        );
    }
  }

  return (
    <>
      {/* {subject.isSuccess ? ( */}
      <div className="p-8">
        <div className="flex justify-end pb-2">
          <Tooltip title="Application Fee settings">
            <Button
              className="application-fee-btn"
              icon={<SettingOutlined />}
              onClick={() => setFeeSettingsOpen(true)}
            >
              Application Fee
            </Button>
          </Tooltip>
        </div>
        <div
          className="bg-white rounded-lg"
          style={{ display: "flex" }}
        >
          <div
            onClick={() => setStep(1)}
            style={{ flex: 1 }}
            className={`iskool-tab-item ${
              step === 1 ? "bg-gray-600 text-white shadow" : ""
            }`}
          >
            <div className="text-lg">Fill Application</div>
          </div>
          <div
            onClick={() => setStep(2)}
            style={{ flex: 1 }}
            className={`iskool-tab-item ${
              step === 2 ? "bg-gray-600 text-white shadow" : ""
            }`}
          >
            <div className="text-lg flex items-center justify-center">
              Pending
              {applications.isSuccess && (
                <TabCount count={pendingCount} color="#f59e0b" />
              )}
            </div>
          </div>
          <div
            onClick={() => setStep(3)}
            style={{ flex: 1 }}
            className={`iskool-tab-item ${
              step === 3 ? "bg-gray-600 text-white shadow" : ""
            }`}
          >
            <div className="text-lg flex items-center justify-center">
              History
              {applications.isSuccess && (
                <TabCount count={historyCount} color="#64748b" />
              )}
            </div>
          </div>
          <div
            onClick={() => setStep(4)}
            style={{ flex: 1 }}
            className={`iskool-tab-item ${
              step === 4 ? "bg-gray-600 text-white shadow" : ""
            }`}
          >
            <div className="text-lg">Offline Application</div>
          </div>
          <div
            onClick={() => setStep(5)}
            style={{ flex: 1 }}
            className={`iskool-tab-item ${
              step === 5 ? "bg-gray-600 text-white shadow" : ""
            }`}
          >
            <div className="text-lg flex items-center justify-center">
              Inquiries
              {inquiryCounts.isSuccess && (
                <TabCount count={openInquiryCount} color="#3b82f6" />
              )}
            </div>
          </div>
        </div>
        {/* ------------------------------------------------------------------------------------------- */}
        <div>
          <div>{switchTab()}</div>
        </div>
      </div>
      <Modal
        title="Application Fee"
        visible={feeSettingsOpen}
        onCancel={() => setFeeSettingsOpen(false)}
        footer={null}
      >
        <div
          className="rounded p-3 mb-4"
          style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}
        >
          <span className="text-xs text-gray-500 uppercase tracking-wide">
            Currently saved
          </span>
          <div className="text-lg font-semibold">
            {schoolQuery.isLoading
              ? "Loading…"
              : `₹${school?.application_fee || 0}`}
          </div>
        </div>
        {!schoolQuery.isLoading && school?.application_fee > 0 && (
          <Alert
            type="warning"
            showIcon
            className="mb-4"
            message="An application fee is already set"
            description="Changing it here won't affect applications already submitted — it only applies to new applications going forward."
          />
        )}
        <Form form={feeForm} layout="vertical" onFinish={handleSaveFee}>
          <Form.Item
            name="application_fee"
            label="Application Fee (applies to every application submitted to this school)"
            rules={[{ required: true, message: "Please enter an amount" }]}
          >
            <InputNumber
              min={0}
              style={{ width: "100%" }}
              placeholder="0 for no fee"
            />
          </Form.Item>
          <p className="text-sm text-gray-500 pb-3">
            Set to 0 if applications shouldn't require a fee. A non-zero fee
            must be marked "Paid" or "Waived" on an application before it can
            be approved.
          </p>
          <Button type="primary" htmlType="submit">
            Save
          </Button>
        </Form>
      </Modal>
    </>
  );
};

export default SwitchAdmission;
